import React, { useCallback, useEffect, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  getFanEnergyApiPayload,
  getFanEnergySeasonLeaderboard,
  getFanEnergySeasons,
  isFanEnergyApiSuccess,
} from '../../services/fanEnergyApi';
import LeaderboardTable from './LeaderboardTable';
import { formatNumber, formatSeasonLabel, PAGE_SIZE } from './fanEnergyLeaderboardUtils';

function SeasonLeaderboardTab() {
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState('');
  const [includeGuests, setIncludeGuests] = useState(false);
  const [offset, setOffset] = useState(0);
  const [entries, setEntries] = useState([]);
  const [season, setSeason] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loadingSeasons, setLoadingSeasons] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadSeasons = useCallback(async () => {
    setLoadingSeasons(true);
    try {
      const result = await getFanEnergySeasons({ limit: 100, offset: 0 });
      if (!isFanEnergyApiSuccess(result)) {
        setError(result?.message || 'Failed to load seasons.');
        setSeasons([]);
        return;
      }
      const data = getFanEnergyApiPayload(result);
      const list = data?.seasons || [];
      setSeasons(list);
      const current = list.find((item) => item.isCurrent) || list[0];
      if (current) setSeasonId(String(current.seasonId));
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load seasons.');
      setSeasons([]);
    } finally {
      setLoadingSeasons(false);
    }
  }, []);

  const loadLeaderboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getFanEnergySeasonLeaderboard({
        seasonId: seasonId || undefined,
        includeGuests,
        limit: PAGE_SIZE,
        offset,
      });
      if (!isFanEnergyApiSuccess(result)) {
        setError(result?.message || 'Failed to load season leaderboard.');
        setEntries([]);
        setSeason(null);
        setPagination(null);
        return;
      }
      const data = getFanEnergyApiPayload(result);
      setEntries(data?.entries || []);
      setSeason(data?.season || null);
      setPagination(data?.pagination || null);
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load season leaderboard.');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [includeGuests, offset, seasonId]);

  useEffect(() => {
    loadSeasons();
  }, [loadSeasons]);

  useEffect(() => {
    if (loadingSeasons) return;
    loadLeaderboard();
  }, [loadLeaderboard, loadingSeasons]);

  const onSeasonChange = (value) => {
    setSeasonId(value);
    setOffset(0);
  };

  const onIncludeGuestsChange = (checked) => {
    setIncludeGuests(checked);
    setOffset(0);
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <TextField
          select
          size="small"
          label="Season"
          value={seasonId}
          onChange={(event) => onSeasonChange(event.target.value)}
          disabled={loadingSeasons || seasons.length === 0}
          sx={{ minWidth: 280 }}
        >
          {seasons.length === 0 ? (
            <MenuItem value="">No seasons</MenuItem>
          ) : (
            seasons.map((item) => (
              <MenuItem key={item.seasonId} value={String(item.seasonId)}>
                {formatSeasonLabel(item)}
              </MenuItem>
            ))
          )}
        </TextField>
        <FormControlLabel
          control={
            <Switch
              checked={includeGuests}
              onChange={(event) => onIncludeGuestsChange(event.target.checked)}
              disabled={loading}
            />
          }
          label="Include guests"
        />
        <Button
          variant="text"
          startIcon={<RefreshIcon />}
          onClick={loadLeaderboard}
          disabled={loading || loadingSeasons}
        >
          Refresh
        </Button>
      </Box>

      {season && (
        <Chip label={formatSeasonLabel(season)} color="primary" variant="outlined" sx={{ mb: 1.5 }} />
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {pagination
              ? `${formatNumber(pagination.total)} entr${pagination.total === 1 ? 'y' : 'ies'}`
              : 'Leaderboard'}
            {includeGuests ? ' · registered users and guests' : ' · registered users only'}
          </Typography>
          <LeaderboardTable entries={entries} showGuestBadge={includeGuests} />
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button disabled={loading || offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              Previous
            </Button>
            <Button disabled={loading || !pagination?.hasMore} onClick={() => setOffset(offset + PAGE_SIZE)}>
              Next
            </Button>
            <Typography variant="body2" color="text.secondary">
              Offset {offset}
            </Typography>
          </Box>
        </>
      )}
    </>
  );
}

export default SeasonLeaderboardTab;
